// NDT Inspection Method Patterns matching service types and technician skills
export const METHOD_PATTERNS = {
    RT: /(?:^|[\s\-_/(\[])(?:RT|RADIOGRAPH(?:Y|IC)?)(?:$|[\s\-_/)\]0-9])/i,
    UT: /(?:^|[\s\-_/(\[])(?:UT|UTG|ULTRASONIC|PAUT|PHASED\s+ARRAY|TOFD)(?:$|[\s\-_/)\]0-9])/i,
    PT: /(?:^|[\s\-_/(\[])(?:PT|DPT|LPT|PENETRANT|DYE\s+PENETRANT)(?:$|[\s\-_/)\]0-9])/i,
    MT: /(?:^|[\s\-_/(\[])(?:MT|MPT|MPI|MAGNETIC(?:\s+PARTICLE)?)(?:$|[\s\-_/)\]0-9])/i,
    VT: /(?:^|[\s\-_/(\[])(?:VT|VISUAL(?:\s+TESTING)?)(?:$|[\s\-_/)\]0-9])/i,
    ET: /(?:^|[\s\-_/(\[])(?:ET|EDDY(?:\s+CURRENT)?)(?:$|[\s\-_/)\]0-9])/i,
};

export const METHOD_LABELS = {
    RT: 'Radiography (RT)',
    UT: 'Ultrasonic Testing (UT)',
    PT: 'Penetrant Testing (PT / DPT)',
    MT: 'Magnetic Particle Testing (MT / MPT)',
    VT: 'Visual Testing (VT)',
    ET: 'Eddy Current Testing (ET)',
};

/**
 * Extract an array of canonical NDT method codes (e.g. ['RT'], ['UT']) from a given text string.
 */
export const detectMethods = (text) => {
    if (!text || typeof text !== 'string') return [];
    const methods = [];
    for (const [code, pattern] of Object.entries(METHOD_PATTERNS)) {
        if (pattern.test(text)) {
            methods.push(code);
        }
    }
    return methods;
};

/**
 * Extract all skill names as flat strings from a technician object.
 * Handles array of objects [{id, name}], array of strings, or categorized dicts.
 */
export const getTechnicianSkillNames = (technician) => {
    if (!technician) return [];

    const names = [];
    const userSkills = technician.skills || (technician.profile && technician.profile.skills);

    if (Array.isArray(userSkills)) {
        userSkills.forEach((item) => {
            if (item && typeof item === 'object' && item.name) {
                names.push(String(item.name));
            } else if (typeof item === 'string' && item.trim()) {
                names.push(item.trim());
            }
        });
    } else if (userSkills && typeof userSkills === 'object') {
        Object.values(userSkills).forEach((val) => {
            if (Array.isArray(val)) {
                val.forEach((item) => {
                    if (item && typeof item === 'object' && item.name) {
                        names.push(String(item.name));
                    } else if (typeof item === 'string' && item.trim()) {
                        names.push(item.trim());
                    }
                });
            } else if (typeof val === 'string' && val.trim()) {
                names.push(val.trim());
            }
        });
    }

    // Deduplicate preserving order
    return Array.from(new Set(names));
};

/**
 * Returns an array of canonical method codes (e.g. ['RT', 'UT']) the technician is qualified for.
 */
export const getTechnicianMethods = (technician) => {
    if (!technician) return [];
    if (Array.isArray(technician.methods) && technician.methods.length > 0) {
        return technician.methods;
    }
    const skillNames = getTechnicianSkillNames(technician);
    const methodsSet = new Set();
    skillNames.forEach((name) => {
        detectMethods(name).forEach((m) => methodsSet.add(m));
    });
    return Array.from(methodsSet);
};

/**
 * Checks whether a technician has the required skill/method for a service type.
 */
export const isTechnicianQualifiedForServiceType = (technician, serviceType) => {
    if (!technician || !serviceType) return true;

    const stName = typeof serviceType === 'object' ? serviceType.name : String(serviceType);
    if (!stName) return true;

    const requiredMethods = detectMethods(stName);
    if (requiredMethods.length === 0) {
        // If no specific NDT method is detected from the service type name, allow assignment
        return true;
    }

    const techMethods = getTechnicianMethods(technician);
    const hasMatchingMethod = requiredMethods.some((m) => techMethods.includes(m));
    if (hasMatchingMethod) {
        return true;
    }

    // Direct name fallback: check if any technician skill matches or is contained in the service type name
    const skillNames = getTechnicianSkillNames(technician);
    const stLower = stName.trim().toLowerCase();
    const hasNameMatch = skillNames.some((sk) => {
        const skLower = sk.trim().toLowerCase();
        return skLower === stLower || stLower.includes(skLower) || skLower.includes(stLower);
    });

    return hasNameMatch;
};

/**
 * Filter a list of technicians based on qualification for a selected service type.
 */
export const filterTechniciansByServiceType = (technicians, serviceType) => {
    if (!Array.isArray(technicians)) return [];
    if (!serviceType) return technicians;

    return technicians.filter((tech) => isTechnicianQualifiedForServiceType(tech, serviceType));
};

/**
 * Check if a technician has any expired certificates according to validityMap.
 */
export const isTechnicianCertificateExpired = (technician, validityMap = null) => {
    if (!technician) return false;
    const techId = typeof technician === 'object' ? technician.id : technician;
    if (validityMap && validityMap[techId]) {
        return Boolean(validityMap[techId].has_expired_certs);
    }
    if (typeof technician === 'object' && technician.has_expired_certs != null) {
        return Boolean(technician.has_expired_certs);
    }
    return false;
};

/**
 * Get expired certificates list for a technician.
 */
export const getTechnicianExpiredCertDetails = (technician, validityMap = null) => {
    if (!technician) return [];
    const techId = typeof technician === 'object' ? technician.id : technician;
    if (validityMap && validityMap[techId]) {
        return validityMap[techId].expired_certs || [];
    }
    return [];
};

/**
 * Format a human-readable option label for autocomplete dropdowns.
 * Flags technicians with expired certificates.
 */
export const formatTechnicianOptionLabel = (tech, validityMap = null) => {
    if (!tech) return '';
    const name = tech.first_name && tech.last_name
        ? `${tech.first_name} ${tech.last_name}`
        : (tech.username || tech.email || 'Unknown');
    const base = tech.email ? `${name} (${tech.email})` : name;
    const isExpired = isTechnicianCertificateExpired(tech, validityMap);
    return isExpired ? `${base} ⚠️ (Certificate Expired - Cannot Assign)` : base;
};

